import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Fab
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalsAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

const PortalConfiguration = () => {
  const [editDialog, setEditDialog] = useState({ open: false, portal: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, portal: null });
  const queryClient = useQueryClient();

  // Fetch portals
  const { data: portalsData, isLoading, error, refetch } = useQuery({
    queryKey: ['portals'],
    queryFn: () => portalsAPI.getAllPortals(),
  });

  // Create portal mutation
  const createMutation = useMutation({
    mutationFn: portalsAPI.createPortal,
    onSuccess: () => {
      queryClient.invalidateQueries(['portals']);
      setEditDialog({ open: false, portal: null });
      toast.success('Portal created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create portal');
    },
  });

  // Update portal mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => portalsAPI.updatePortal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['portals']);
      setEditDialog({ open: false, portal: null });
      toast.success('Portal updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update portal');
    },
  });

  // Delete portal mutation
  const deleteMutation = useMutation({
    mutationFn: portalsAPI.deletePortal,
    onSuccess: () => {
      queryClient.invalidateQueries(['portals']);
      setDeleteDialog({ open: false, portal: null });
      toast.success('Portal deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete portal');
    },
  });

  const portals = portalsData?.data || [];

  const handleEdit = (portal) => {
    setEditDialog({ open: true, portal });
  };

  const handleDelete = (portal) => {
    setDeleteDialog({ open: true, portal });
  };

  // const handleViewPortal = (portal) => {
  //   const url = `https://woodapples.in/projects/hitpa-portals/${portal.id}` || portal.urls?.development;
  //   window.open(url, '_blank');
  // };

   const handleViewPortal = (portal) => {
    const url = `https://qa-hitpa.consint.ai/api/portals-service/${portal.id}` || portal.urls?.development;
    window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load portals: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Portal Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure and manage portal settings, themes, and features
          </Typography>
        </Box>
        {/* <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Fab
            color="primary"
            size="medium"
            onClick={() => setEditDialog({ open: true, portal: null })}
          >
            <AddIcon />
          </Fab>
        </Box> */}
      </Box>
      <Grid container spacing={3} columns={12}>
        {portals.data?.map((portal) => (
          <Grid item size={{xs:12, sm:6, md:4}} key={portal.id}>
            <Card sx={{ height:'100%', display:'flex', flexDirection:'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SettingsIcon sx={{ color: portal.color, mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {portal.name}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {portal.description}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={portal.status}
                    size="small"
                    color={portal.status === 'active' ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: portal.color,
                      border: '2px solid #fff',
                      boxShadow: 1
                    }}
                  />
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewPortal(portal)}
                >
                  View
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(portal)}
                >
                  Edit
                </Button>
                {/* <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(portal)}
                >
                  <DeleteIcon />
                </IconButton> */}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {portals.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No portals found. Click the + button to create your first portal.
        </Alert>
      )}

      {/* Edit/Create Portal Dialog */}
      <PortalDialog
        open={editDialog.open}
        portal={editDialog.portal}
        onClose={() => setEditDialog({ open: false, portal: null })}
        onSave={(data) => {
          if (editDialog.portal) {
            updateMutation.mutate({ id: editDialog.portal._id, data });
          } else {
            createMutation.mutate(data);
          }
        }}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, portal: null })}
      >
        <DialogTitle>Delete Portal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.portal?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, portal: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteDialog.portal._id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Portal Dialog Component
const PortalDialog = ({ open, portal, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState({
    id: '',
    type: '',
    name: '',
    description: '',
    status: 'active',
    color: '#1976d2'
  });

  useEffect(() => {
    if (portal) {
      setFormData({
        id: portal.id || '',
        type: portal.type || '',
        name: portal.name || '',
        description: portal.description || '',
        status: portal.status || 'active',
        color: portal.color || '#1976d2'
      });
    } else {
      setFormData({
        id: '',
        type: '',
        name: '',
        description: '',
        status: 'active',
        color: '#1976d2'
      });
    }
  }, [portal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const portalTypes = ['member', 'corporate', 'provider', 'intermediary', 'insurer', 'irda', 'admin'];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {portal ? 'Edit Portal' : 'Create New Portal'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Portal ID"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                required
                disabled={!!portal}
                helperText={portal ? "ID cannot be changed" : "Unique identifier for the portal"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Portal Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Portal Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  disabled={!!portal}
                >
                  {portalTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Portal Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Theme Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (portal ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PortalConfiguration;
